import type { RequestContext } from "../../../application/context/request-context.js";
import type { DocumentHold } from "../../../domain/review/document-hold.js";

const STORAGE_KEY = "document_holds";

export interface DocumentHoldRepository {
  findById(context: RequestContext, id: string): Promise<DocumentHold | null>;
  /** O HOLD ativo do documento, se houver (só existe um por vez). */
  findActiveByDocument(context: RequestContext, documentType: string, documentId: string): Promise<DocumentHold | null>;
  listByDocument(context: RequestContext, documentType: string, documentId: string): Promise<readonly DocumentHold[]>;
  listAll(context: RequestContext): Promise<readonly DocumentHold[]>;
  save(context: RequestContext, hold: DocumentHold): Promise<void>;
}

/** Espelhado em localStorage, como os demais repositórios do app real. */
export class InMemoryDocumentHoldRepository implements DocumentHoldRepository {
  private holds = new Map<string, DocumentHold>();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed: DocumentHold[] = JSON.parse(stored);
          for (const h of parsed) this.holds.set(h.id, h);
        }
      }
    } catch (e) {
      console.warn("Failed to load document holds from storage:", e);
    }
  }

  private persistStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(this.holds.values())));
      }
    } catch (e) {
      console.error("Failed to persist document holds to storage:", e);
      throw e;
    }
  }

  async findById(_context: RequestContext, id: string): Promise<DocumentHold | null> {
    this.loadFromStorage();
    return this.holds.get(id) || null;
  }

  async findActiveByDocument(_context: RequestContext, documentType: string, documentId: string): Promise<DocumentHold | null> {
    this.loadFromStorage();
    return Array.from(this.holds.values()).find(h => h.documentType === documentType && h.documentId === documentId && h.status === "EM_HOLD") || null;
  }

  async listByDocument(_context: RequestContext, documentType: string, documentId: string): Promise<readonly DocumentHold[]> {
    this.loadFromStorage();
    return Array.from(this.holds.values()).filter(h => h.documentType === documentType && h.documentId === documentId).sort((a, b) => a.placedAt.localeCompare(b.placedAt));
  }

  async listAll(): Promise<readonly DocumentHold[]> {
    this.loadFromStorage();
    return Array.from(this.holds.values());
  }

  async save(_context: RequestContext, hold: DocumentHold): Promise<void> {
    this.holds.set(hold.id, { ...hold });
    this.persistStorage();
  }
}
